'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import TadmLogo from '@/components/tadm-logo';
import { LogOut } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { logger } from '@/lib/client-logger';
import { useToast } from '@/hooks/use-toast';
import { getCurrentUserRole, roleToRedirectPath, signInAndGetRole } from '@/lib/auth';

const formSchema = z.object({
  email: z.string().min(1, 'Officer ID is required'),
  password: z.string().min(1, 'Password is required'),
});

export default function OfficerLoginPage() {
  const router = useRouter();
  const { toast } = useToast();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // Mount/unmount logs
  useEffect(() => {
    // If already logged in, redirect by role
    (async () => {
      try {
        const current = await getCurrentUserRole();
        if (current) {
          const url = roleToRedirectPath(current.role, current.email);
          router.replace(url);
          return;
        }
      } catch (_) {}
    })();

    logger.info('[OfficerLogin] mounted', {}, 'OfficerLogin');
    const onWindowError = (e: ErrorEvent) => {
      logger.error('[OfficerLogin] window error', { message: e.message, filename: e.filename, lineno: e.lineno, colno: e.colno, stack: (e.error && (e.error as any).stack) || undefined }, 'OfficerLogin');
    };
    const onUnhandledRejection = (e: PromiseRejectionEvent) => {
      logger.error('[OfficerLogin] unhandledrejection', { reason: String(e.reason), stack: (e.reason && (e.reason as any).stack) || undefined }, 'OfficerLogin');
    };
    window.addEventListener('error', onWindowError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => {
      logger.info('[OfficerLogin] unmounted', {}, 'OfficerLogin');
      window.removeEventListener('error', onWindowError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);

  // Log form changes (sanitized)
  useEffect(() => {
    const subscription = form.watch((value, { name, type }) => {
      const safe = { officerId: (value as any).email ?? '', password: value.password ? '******' : '' };
      logger.debug('[OfficerLogin] form change', { name, type, value: safe }, 'OfficerLogin');
    });
    return () => subscription?.unsubscribe?.();
  }, [form]);

  async function onSubmit(values: z.infer<typeof formSchema>) {
      const sanitizedValues = { ...values, password: '*****' };
      logger.info('[OfficerLogin] submit', sanitizedValues, 'OfficerLogin');
      try {
        const emailAddr = values.email.includes('@') ? values.email : `${values.email}@tadm.com`;
        const { role } = await signInAndGetRole(emailAddr, values.password);
        logger.info('[OfficerLogin] firebase login success', { role, officerId: values.email }, 'OfficerLogin');

        if (role === 'admin') {
          const qs = new URLSearchParams({ officerId: values.email }).toString();
          const url = `/admin/dashboard?${qs}`;
          logger.debug('[OfficerLogin] navigating to admin dashboard', { url }, 'OfficerLogin');
          router.push(url);
          return;
        }

        if (role === 'officer') {
          const qs = new URLSearchParams({ officerId: values.email }).toString();
          const url = `/officer/dash?${qs}`;
          logger.debug('[OfficerLogin] navigating to officer dashboard', { url }, 'OfficerLogin');
          router.push(url);
          return;
        }

        // Any other valid role (e.g., visitor) is not allowed for this page
        toast({ title: 'Access denied', description: 'Your role does not have access to the Officer portal.', variant: 'destructive' });
      } catch (e: any) {
        const message = e?.message || 'Login failed. Please try again.';
        logger.error('[OfficerLogin] login failed', { error: String(message) }, 'OfficerLogin');
        toast({ title: 'Login error', description: message, variant: 'destructive' });
      }
  }

  function onInvalid(errors: any) {
    logger.warn('[OfficerLogin] validation errors', { errors }, 'OfficerLogin');
  }

  return (
    <main className="relative flex min-h-screen flex-col items-center justify-center bg-gray-50 p-4 dark:bg-gray-900">
      <div className="w-full max-w-md">
        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 h-16 w-16">
              <TadmLogo />
            </div>
            <CardTitle className="font-headline text-3xl">Officer Login</CardTitle>
            <CardDescription>Enter your details to login.</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Officer ID</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="e.g., officer1" autoComplete="username" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input type="password" placeholder="Enter your password" autoComplete="current-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                 <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
                  {form.formState.isSubmitting ? 'Starting...' : 'Start Session'}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
